// Backend contract (ev-backoffice-api PS-345, KioskFlowController) — one flow per operator:
// GET  /kiosk-flow/operators/{operatorId}/flow          → KioskFlowApiResponse (flow is null when none exists)
// POST /kiosk-flow/operators/{operatorId}/flow { name } → KioskFlowApiResponse
// PUT  /kiosk-flow/operators/{operatorId}/flow { name, promoBannerFileName?, screenFlow? } → KioskFlowApiResponse
//      (screenFlow omitted → rename only, the stored draft is kept; screenFlow sent → draft replaced)
// DELETE /kiosk-flow/operators/{operatorId}/flow        → KioskFlowApiResponse (flow is null)
// Business failures (operator already has a flow, validation, not admin) come back
// as HTTP 200 with isSuccess=false and errorMessage set.

// Matches TBL_KioskFlows.Name NVARCHAR(200)
export const MAX_FLOW_NAME_LENGTH = 200;

// Steps a kiosk flow can use — fixed list, each step can appear once per flow
export const KIOSK_FLOW_STEPS = [
  'Loading',
  'Connectors',
  'Phone',
  'LicensePlate',
  'Pricing',
  'AuthAmount',
  'Payment',
  'Charging',
  'ThankYou',
  'StopCharge',
  'Ending',
] as const;

// The kiosk's default timeout; left out of the saved JSON when it equals this
export const DEFAULT_TIMEOUT_SECONDS = 30;
// Same cap as the backoffice validator (MaxTimeoutMs = 3_600_000)
export const MAX_TIMEOUT_SECONDS = 3600;
// Same cap as the backoffice validator (MaxBannerFileNameLength)
export const MAX_BANNER_FILE_NAME_LENGTH = 255;
// Steps a flow must have before it can be published (backoffice KioskFlowSteps.Required)
export const REQUIRED_FLOW_STEPS = ['Loading', 'Connectors', 'Payment', 'Charging'] as const;

// One screenFlow entry as stored by the backend (timeoutDuration is in ms)
export interface KioskFlowStep {
  step: string;
  layoutName: string;
  timeoutTarget?: string | null;
  timeoutDuration?: number | null;
}

export interface KioskFlow {
  flowId: number;
  operatorId: number;
  name: string;
  // Image in the operator's Asset Manager images folder, e.g. "banner_v1.png" — the kiosk builds the URL
  promoBannerFileName?: string | null;
  screenFlow?: KioskFlowStep[];
  createdAt?: string;
  createdByName?: string | null;
  updatedAt?: string;
  updatedByName?: string | null;
  lastPublishedAt?: string | null;
  lastPublishedByName?: string | null;
}

export interface KioskFlowApiResponse {
  isSuccess: boolean;
  errorMessage?: string | null;
  flow?: KioskFlow | null;
}

export interface KioskFlowNameRequest {
  name: string;
}

export interface KioskFlowSaveRequest {
  name: string;
  promoBannerFileName?: string | null;
  screenFlow: KioskFlowStep[];
}

// Editable step row. Kept as strings so fields can be edited in place;
// timeoutSeconds '' means the default (30s).
export interface DraftStep {
  // Local row key only — never sent to the backend
  id: string;
  step: string;
  layoutName: string;
  timeoutTarget: string;
  timeoutSeconds: string;
}

// One reason the draft can't be saved or published, found in the editor before any request.
// rowId / stepIndex are set for row issues; flow-level issues (required steps, banner) have neither.
export interface DraftIssue {
  rowId?: string;
  stepIndex?: number;
  field: 'step' | 'layoutName' | 'timeoutTarget' | 'timeoutSeconds' | 'screenFlow' | 'promoBannerFileName';
  message: string;
  // 'save' blocks Save and Publish; 'publish' only blocks Publish
  blocks: 'save' | 'publish';
}

// An XML layout from Asset Manager — one entry per name across all screen sizes
export interface KioskLayoutOption {
  // File name without .xml, e.g. "fragment_loading_paz"
  layoutName: string;
  // Screen-size folders the file was found in (mdpi / hdpi / xhdpi)
  sizes: string[];
}

// Subset of the Asset Manager list item this feature reads (XML layouts and images)
export interface KioskAssetItem {
  // Title shown in Asset Manager, e.g. "Summer promo"
  name?: string | null;
  currentFileName: string;
  currentS3Url: string;
}

// Promo banner choice: shown by its Asset Manager title, saved by its file name
export interface KioskImageOption {
  // e.g. "banner_v1.png" — what the flow stores and the kiosk loads
  fileName: string;
  // Asset Manager title; the file name when the asset has none
  title: string;
}

export interface KioskAssetListResponse {
  isSuccess?: boolean;
  errorMessage?: string | null;
  items?: KioskAssetItem[];
  totalCount?: number;
}

// Publish (ev-backoffice-api PS-348):
// POST /kiosk-flow/operators/{operatorId}/flow/publish/prepare → KioskFlowPublishPrepareResponse (writes nothing)
// POST /kiosk-flow/operators/{operatorId}/flow/versions { jsonSnapshot } → KioskFlowVersionResponse
//      (errorCode "Conflict" when the draft changed since prepare)

// One reason the flow can't be published. stepIndex is 0-based, null for flow-level errors.
export interface KioskFlowValidationError {
  stepIndex?: number | null;
  field?: string | null;
  message: string;
}

export interface KioskFlowPublishPrepareResponse {
  isSuccess: boolean;
  errorMessage?: string | null;
  errorCode?: string | number | null;
  // The JSON to push to the kiosks — sent back unchanged when recording the version
  json?: string | null;
  validationErrors?: KioskFlowValidationError[] | null;
}

export interface KioskFlowVersion {
  versionId: number;
  version: number;
  publishedAt: string;
  publishedByName?: string | null;
  isRollback: boolean;
  rollbackFromVersion?: number | null;
  jsonSnapshot?: string | null;
}

export interface KioskFlowVersionResponse {
  isSuccess: boolean;
  errorMessage?: string | null;
  errorCode?: string | number | null;
  version?: KioskFlowVersion | null;
  flow?: KioskFlow | null;
  validationErrors?: KioskFlowValidationError[] | null;
}

export interface KioskFlowRecordVersionRequest {
  jsonSnapshot: string;
}

export interface KioskFlowPublishResult {
  version: KioskFlowVersion;
  flow: KioskFlow;
}

// Kiosk API operator record (DbActor) — GET /admin/operator, POST /admin/addOperator.
// Written back whole with only settings changed, so the other fields are kept.
export interface KioskOperatorRecord {
  id: number;
  parentId?: number | null;
  countryCode?: string | null;
  partyId?: string | null;
  // The operator's kiosk settings as a JSON string; the flow lives under screenFlow / promoBannerFileName
  settings?: string | null;
  csms?: string | null;
}

// Operator the flow belongs to. Passed explicitly because the page runs under
// AuthOnlyRoute, so the global selectedOperator is not guaranteed to exist.
export interface KioskFlowOperatorContext {
  operatorId: number;
  appId: number;
}
